import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Pin, Lock, Trash2, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ForumTopic = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canModerate = user?.role === 'Administrador' || user?.role === 'Formador';

  useEffect(() => {
    fetchTopic();
  }, [topicId]);

  const fetchTopic = async () => {
    try {
      const response = await axios.get(`${API_URL}/forums/topics/${topicId}`);
      setTopic(response.data);
    } catch (error) {
      toast.error('Error al cargar el tema');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/forums/topics/${topicId}/replies`, {
        content: replyContent
      });
      toast.success('Respuesta añadida');
      setReplyContent('');
      fetchTopic();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir respuesta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm('¿Eliminar esta respuesta?')) return;
    
    try {
      await axios.delete(`${API_URL}/forums/replies/${replyId}`);
      toast.success('Respuesta eliminada');
      fetchTopic();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  if (!topic) return null;

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/forums/course/${topic.online_course_id}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al foro
          </Button>
        </div>

        {/* Topic */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              {topic.is_pinned && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                  <Pin className="w-3 h-3" />
                  Fijado
                </span>
              )}
              {topic.is_closed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                  <Lock className="w-3 h-3" />
                  Cerrado
                </span>
              )}
            </div>
            <h1 className="font-manrope font-bold text-2xl text-slate-900 mb-4">
              {topic.title}
            </h1>
            <div className="prose prose-slate max-w-none">
              <p className="whitespace-pre-wrap">{topic.content}</p>
            </div>
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#004E89] text-white flex items-center justify-center font-semibold">
                  {topic.user_name?.charAt(0) || 'U'}
                </div>
                <span className="font-medium text-slate-700">{topic.user_name}</span>
              </div>
              <span>•</span>
              <span>{formatDate(topic.created_at)}</span>
            </div>
          </div>

          {/* Replies */}
          <div className="divide-y divide-slate-100">
            {topic.replies?.map((reply) => (
              <div key={reply.id} className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-semibold">
                        {reply.user_name?.charAt(0) || 'U'}
                      </div>
                      <span className="font-medium text-slate-700 text-sm">{reply.user_name}</span>
                      <span className="text-xs text-slate-400">•</span>
                      <span className="text-xs text-slate-400">{formatDate(reply.created_at)}</span>
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{reply.content}</p>
                  </div>
                  {(canModerate || reply.user_id === user?.id) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteReply(reply.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Reply Form */}
          {!topic.is_closed ? (
            <div className="p-6 bg-slate-50 border-t border-slate-200">
              <form onSubmit={handleSubmitReply} className="space-y-4">
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Escribe tu respuesta..."
                  rows={3}
                  required
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="bg-[#004E89] hover:bg-[#003B66]"
                    disabled={submitting || !replyContent.trim()}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? 'Enviando...' : 'Responder'}
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <div className="p-6 bg-orange-50 border-t border-orange-200 text-center">
              <Lock className="w-6 h-6 text-orange-500 mx-auto mb-2" />
              <p className="text-orange-700">Este tema está cerrado y no admite más respuestas.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ForumTopic;
